/**
 * @file: SectionCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { MeIcon, MeIconName } from '@euler/app/flows/me/components/MeIcon';
import { MK, cardShadowStyle } from '@euler/app/flows/me/components/meKit';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReactNode, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const SectionCard = memo(
  ({
    title,
    icon,
    badge,
    action,
    children,
  }: {
    title: string;
    icon?: MeIconName;
    badge?: string;
    action?: ReactNode;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={[
          {
            backgroundColor: MK.surface,
            borderRadius: rpx(16),
            borderWidth: 1,
            borderColor: MK.hairline,
            padding: rpx(16),
          },
          cardShadowStyle(rpx),
        ]}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: rpx(8),
            minHeight: rpx(32),
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {icon != null && (
              <View
                style={{
                  width: rpx(32),
                  height: rpx(32),
                  borderRadius: rpx(8),
                  backgroundColor: MK.brandSoft,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: rpx(10),
                }}
              >
                <MeIcon name={icon} size={rpx(16)} color={MK.brand} />
              </View>
            )}
            <Typography.Text size={rpx(17)} color={MK.ink} bold>
              {title}
            </Typography.Text>
            {badge != null && (
              <View
                style={{
                  marginLeft: rpx(8),
                  borderRadius: rpx(999),
                  backgroundColor: MK.muted,
                  paddingHorizontal: rpx(8),
                  paddingVertical: rpx(1),
                }}
              >
                <Typography.Text size={rpx(11)} color={MK.mutedInk}>
                  {badge}
                </Typography.Text>
              </View>
            )}
          </View>
          {action}
        </View>
        {children}
      </View>
    );
  },
);

/** Text link for a card header; `chevron` marks it as navigating away. */
export const SectionLink = memo(
  ({
    label,
    chevron,
    onPress,
  }: {
    label: string;
    chevron?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        onPress={onPress}
        hitSlop={PredefinedHitSlops.large}
        style={{ flexDirection: 'row', alignItems: 'center' }}
      >
        <Typography.Text size={rpx(13)} color={MK.brand} medium>
          {label}
        </Typography.Text>
        {chevron && (
          <MeIcon
            name="alt-arrow-right"
            size={rpx(14)}
            color={MK.brand}
            style={{ marginLeft: rpx(2) }}
          />
        )}
      </TouchableOpacity>
    );
  },
);

/** Compact filled button for a card header's primary action. */
export const SectionButton = memo(
  ({ label, onPress }: { label: string; onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        onPress={onPress}
        hitSlop={PredefinedHitSlops.medium}
        activeOpacity={0.8}
        style={{
          borderRadius: rpx(8),
          backgroundColor: MK.brand,
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(6),
        }}
      >
        <Typography.Text size={rpx(13)} color={MK.onBrand} medium>
          {label}
        </Typography.Text>
      </TouchableOpacity>
    );
  },
);
